import { ReactComponent as Spinner } from "assets/images/spinner-s.svg"
import { ReactNode, useEffect, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import { amplitudeClient } from "lib/amplitude"

const EXPERIMENT_NAME = "w2a_female_pages_exp"

const REDIRECT_VARIANTS: string[] = [
  "v132",
  "v133",
  "v134",
  "v135",
  "v136",
  "v137",
  "v138",
  "v139",
  "v140",
  "v141",
]

type GateStatus = "loading" | "passthrough" | "redirect"

type RedirectTarget = { pathname: string; search: string }

function isFemaleCampaign() {
  const searchParams = new URL(location.href).searchParams
  const campaign = (
    searchParams.get("c") ||
    searchParams.get("utm_campaign") ||
    ""
  ).toLowerCase()

  return campaign.includes("female")
}

function buildSearchWithAfSub(variantName: string) {
  const searchParams = new URL(location.href).searchParams
  searchParams.set("af_sub4", EXPERIMENT_NAME)
  searchParams.set("af_sub5", variantName)

  return `?${searchParams.toString()}`
}

export function FemalePagesABGate({ children }: { children: ReactNode }) {
  const { locale = "en" } = useParams()
  const shouldGate = locale === "en" && isFemaleCampaign()

  const [status, setStatus] = useState<GateStatus>(
    shouldGate ? "loading" : "passthrough",
  )
  const [redirectTo, setRedirectTo] = useState<RedirectTarget | null>(null)

  useEffect(() => {
    if (!shouldGate) return

    let active = true

    const fetchVariant = async () => {
      try {
        await amplitudeClient.fetchExperimentOptions([EXPERIMENT_NAME])
        const variant =
          await amplitudeClient.fetchExperimentVariant(EXPERIMENT_NAME)
        const value = variant?.value

        if (!active) return

        if (value && REDIRECT_VARIANTS.includes(value)) {
          setRedirectTo({
            pathname: `/${locale}/n${value}`,
            search: buildSearchWithAfSub(value),
          })
          setStatus("redirect")
          return
        }

        if (value === "control") {
          const url = new URL(location.href)
          url.searchParams.set("af_sub4", EXPERIMENT_NAME)
          url.searchParams.set("af_sub5", "control")
          window.history.replaceState(window.history.state, "", url.toString())
        }

        setStatus("passthrough")
      } catch {
        if (active) setStatus("passthrough")
      }
    }

    fetchVariant()

    return () => {
      active = false
    }
  }, [])

  if (!shouldGate || status === "passthrough") {
    return <>{children}</>
  }

  if (status === "redirect" && redirectTo) {
    return <Navigate replace to={redirectTo} />
  }

  return (
    <div className="max-w-[450px] mx-auto pt-[72px] px-4 pb-10 flex items-center justify-center h-full">
      <Spinner className="animate-spin text-black" />
    </div>
  )
}
